// react
import { useContext, useEffect, useMemo, useState } from 'react';

// router
import { useParams, useSearchParams } from 'react-router-dom';

// i18n
import { useTranslation } from 'react-i18next';

// mui
import { Alert, Button, Container, Grid, Skeleton, Typography } from '@mui/material';

// layouts
import { Authenticated } from '../../layouts/Authenticated/Authenticated';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// context
import { ContextBanner, ContextUser } from '../../../App.context';
import { ContextProjects } from '../../layouts/Providers/Providers.context';

// types
import {
	AiDocument,
	Entity,
	Condition,
	ContractV2,
	Invoice,
	Voucher,
	Pagination,
} from '../../../@types';

// context
import { ContextInvoices } from './Contract.context';

// child
import { Details } from './Details/Details';
import { Documents } from './Documents/Documents';
import { Invoices } from './Invoices/Invoices';
import { Milestones } from './Milestones/Milestones';
import { PaymentProgress } from './PaymentProgress/PaymentProgress';

// dialogs
import { DialogAddInvoice } from '../../dialogs/DialogAddInvoice/DialogAddInvoice';

// config
import { featureFlags } from '../../../config/featureFlags';

export const PageContract = () => {
	// hooks
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();
	const { id } = useParams<{ id: string }>();
	const { setBanner } = useContext(ContextBanner);
	const { user } = useContext(ContextUser);
	const { projects } = useContext(ContextProjects);

	// params
	const paramDialog = searchParams.get('dialog');

	// vars
	const apiUrl = process.env.REACT_APP_API_URL;
	const token = sessionStorage.getItem('token');
	const authHeaders = {
		Authorization: `Bearer ${token}`,
		apikey: process.env.REACT_APP_APIKEY || '',
	};

	// state
	const [contract, setContract] = useState<ContractV2 | null>(null);
	const [documents, setDocuments] = useState<AiDocument[]>([]);
	const [invoices, setInvoices] = useState<Invoice[]>([]);
	const [paginationInvoices, setPaginationInvoices] = useState<Pagination>({
		page: 1,
		perPage: 10,
		total: undefined,
		totalPages: undefined,
	});
	const [notFound, setNotFound] = useState(false);

	// contract — milestones (conditions) and vouchers ship inline in the
	// V2 contract-by-id response, so there are no separate fetches.
	const { fetchRequest: fetchContract, isLoading: isLoadingContract } = useFetch({
		isLazy: true,
		options: { headers: authHeaders },
		url: `${apiUrl}/api/ProxiesV2/contracts/${id}`,
		onSuccess: (res) => {
			const row = Array.isArray(res?.data) ? res.data[0] : res?.data;
			if (row) {
				setContract(row);
				setNotFound(false);
				// The contract-by-id payload now carries the full invoice list inline
				// (and each milestone's linked slice), so feed the invoices state from
				// it instead of a separate round trip.
				if (Array.isArray(row.invoices)) {
					setInvoices(row.invoices);
				}
			} else {
				setNotFound(true);
			}
		},
		onError: () => setNotFound(true),
	});

	// ai documents tagged to this contract
	const { fetchRequest: fetchDocuments, isLoading: isLoadingDocuments } = useFetch({
		isLazy: true,
		options: { headers: authHeaders },
		url: `${apiUrl}/api/AiProxies/get_documents?tags=contract:${id}&populate=uploaded_by`,
		onSuccess: (res) => {
			const rows = res?.documents ?? res?.data ?? [];
			if (Array.isArray(rows)) {
				setDocuments(rows);
			}
		},
	});

	// invoices linked to this contract
	const {
		fetchRequest: fetchInvoices,
		isLoading: isLoadingInvoices,
		refetch: refetchInvoices,
	} = useFetch({
		isLazy: true,
		options: { headers: authHeaders },
		url: `${apiUrl}/api/ProxiesV2/invoices?contract=${id}`,
		onSuccess: (res) => {
			if (res.data.length) {
				setInvoices(res.data);
			}
		},
	});

	// Pick the supplier entity id this user can act on for the given contract.
	// Prefers the intersection of (user.entities of type SUPPLIER) and
	// (contract.entities of type SUPPLIER); falls back to the user's first
	// supplier entity if the contract's entity list is empty.
	const resolveSupplierEntityId = (
		contract: ContractV2,
		userEntities: Entity[]
	): number | undefined => {
		const userSupplierIds = userEntities.filter((e) => e.type === 'SUPPLIER').map((e) => e.id);
		const contractSupplierIds = (contract.entities || [])
			.filter((e) => (e.type || '').toUpperCase() === 'SUPPLIER')
			.map((e) => e.id);
		const match = contractSupplierIds.find((cid) => userSupplierIds.includes(cid));
		return match ?? userSupplierIds[0];
	};

	const conditions: Condition[] = useMemo(() => contract?.conditions ?? [], [contract?.conditions]);
	const vouchers: Voucher[] = useMemo(() => contract?.vouchers ?? [], [contract?.vouchers]);

	// Payment-bearing conditions on this contract. Additional requirements
	// (sourced from TEMPLATE conditions matching the contract activity's
	// component / category / expense-type classification) are NOT separate
	// milestone rows — they are composed by the API into each milestone's
	// verificationSections array, distinguished by `section.source === 'TEMPLATE'`
	// and a `sourceConditionName` label. The MilestonesCard renders them inline
	// alongside the milestone's own sections.
	const paymentMilestones = useMemo(
		() =>
			conditions
				.filter((c) => c.conditionType === 'payment')
				.sort((a, b) => a.sequenceOrder - b.sequenceOrder),
		[conditions]
	);

	// Contracts can carry multiple `contract_amount` rows in different
	// currencies. These are treated as FX equivalents of the same commitment
	// (not separate amounts to be summed), so the page anchors to a single
	// primary currency — the first amount row — and displays others as "≈"
	// equivalents on the contract-amount field. Every calculation on the page
	// (progress %, paid total, derived milestone amounts) runs in the primary
	// currency so the numbers always agree with the label they sit under.
	const primaryAmount = contract?.amounts?.[0];
	const primaryCurrency = primaryAmount?.currency || 'USD';
	const primaryCommitted = contract?.commitmentAmount ?? primaryAmount?.amount ?? 0;

	// A milestone is "paid" from the supplier's perspective the moment the
	// linked voucher is REDEEMED — that's the on-chain settlement event.
	// The condition row stays in PENDING until WBG runs verification, but
	// the supplier doesn't care about that workflow step.
	const paidConditionIds = useMemo(() => {
		const ids = new Set<number>();
		vouchers.forEach((v) => {
			if (v.condition != null && (v.status || '').toUpperCase() === 'REDEEMED') {
				ids.add(v.condition);
			}
		});
		return ids;
	}, [vouchers]);
	const paidMilestoneCount = paymentMilestones.filter((m) => paidConditionIds.has(m.id)).length;

	// "Paid" only sums vouchers denominated in the primary currency. Vouchers
	// in other currencies would be real, distinct payment events (not FX
	// equivalents of each other) and lumping them together would produce a
	// meaningless total under a single currency label.
	const totalPaid = useMemo(
		() =>
			vouchers
				.filter(
					(v) =>
						(v.status || '').toUpperCase() === 'REDEEMED' &&
						(v.currency || 'USD') === primaryCurrency
				)
				.reduce((sum, v) => sum + (v.amount || 0), 0) ||
			contract?.totalPaid ||
			0,
		[vouchers, primaryCurrency, contract?.totalPaid]
	);
	const progressPct =
		primaryCommitted > 0 ? Math.min(100, (totalPaid / primaryCommitted) * 100) : 0;

	const isContractV2 = projects.some((p) => p.id === contract?.project && p.version === '2');

	// Map each fetched AI document's id to its blob_url so milestone invoice
	// cards can turn an attachment's id into a viewable file link.
	const blobUrlByDocId = useMemo(() => {
		const map = new Map<string, string>();
		documents.forEach((d) => {
			if (d.id != null && d.blob_url) {
				map.set(String(d.id), d.blob_url);
			}
		});
		return map;
	}, [documents]);

	// Fetch contract, documents, and invoices when the page loads or when the token or id changes
	useEffect(() => {
		if (token && id) {
			fetchContract();
			fetchDocuments();
			fetchInvoices();
		}
	}, [fetchContract, fetchDocuments, fetchInvoices, id, token]);

	return (
		<ContextInvoices.Provider
			value={{
				invoices,
				isLoadingInvoices,
				paginationInvoices,
				refetchInvoices,
				setInvoices,
				setPaginationInvoices,
			}}>
			<Authenticated>
				<Container maxWidth={false}>
					<Grid container spacing={2}>
						{notFound && !contract && !isLoadingContract && (
							<Grid item xs={12}>
								<Alert severity="error" variant="filled">
									{t('error.pageNotFound')}
								</Alert>
							</Grid>
						)}

						{isLoadingContract && !contract && (
							<Grid item xs={12}>
								<Skeleton height={140} variant="rounded" />
							</Grid>
						)}

						{contract && (
							<>
								<Grid item xs={12} container justifyContent="space-between" alignItems="flex-end">
									<Typography color="common.white" variant="h2">
										{t('contract')} {contract.num}
									</Typography>
									{featureFlags['CONTRACT_WORKFLOW'] && !isContractV2 && (
										<Button
											color="primary"
											onClick={() => {
												searchParams.set('dialog', 'addInvoice');
												setSearchParams(searchParams);
											}}
											size="small"
											variant="contained">
											{t('submitInvoice')}
										</Button>
									)}
								</Grid>
								<Grid item xs={12}>
									<Details contract={contract} />
								</Grid>

								{featureFlags['CONTRACT_WORKFLOW'] && !isContractV2 && (
									<Grid item xs={12}>
										<Invoices />
									</Grid>
								)}

								{isContractV2 && (
									<>
										<Grid item xs={12}>
											<PaymentProgress
												progressPct={progressPct}
												totalCommitted={primaryCommitted}
												totalPaid={totalPaid}
												currency={primaryCurrency}
												milestonesCompleted={paidMilestoneCount}
												totalPaymentMilestones={
													paymentMilestones.length || contract.totalPaymentMilestones
												}
											/>
										</Grid>
										<Grid item xs={12}>
											<Milestones
												isLoading={isLoadingContract && !contract}
												contract={contract}
												title={t('paymentMilestones')}
												milestones={paymentMilestones}
												vouchers={vouchers}
												paidConditionIds={paidConditionIds}
												currency={primaryCurrency}
												supplierEntityId={resolveSupplierEntityId(
													contract,
													(user?.entities as Entity[] | undefined) || []
												)}
												invoices={invoices}
												blobUrlByDocId={blobUrlByDocId}
												documents={documents}
												onChanged={async (result) => {
													setBanner({
														color: 'success',
														hasClose: true,
														message: result?.draft
															? t('alert.draftSaved')
															: t('alert.milestoneSubmitted'),
													});
													// The banner renders at the top of the page; a milestone can
													// sit well below the fold, so bring the result into view.
													window.scrollTo({ top: 0, behavior: 'smooth' });
													// Await the contract refetch so linkedVouchers reflects a
													// just-created voucher before the supplier can act again. The
													// same payload re-hydrates invoices (including the milestone's
													// linked slice), so no separate invoice fetch is needed.
													await fetchContract();
													fetchDocuments();
												}}
												onError={(message) => {
													setBanner({
														color: 'error',
														hasClose: true,
														message: message || t('error.default'),
													});
													window.scrollTo({ top: 0, behavior: 'smooth' });
												}}
											/>
										</Grid>
										<Grid item xs={12}>
											<Documents documents={documents} isLoading={isLoadingDocuments} />
										</Grid>
									</>
								)}
								<DialogAddInvoice
									contract={{
										id: Number(contract.id),
										project: contract.project,
										num: contract.num ?? String(contract.id),
										amounts: contract.amounts ?? [],
									}}
									isOpen={paramDialog === 'addInvoice'}
									onClose={() => {
										searchParams.delete('dialog');
										setSearchParams(searchParams);
									}}
									onSuccess={() => {
										searchParams.delete('dialog');
										setSearchParams(searchParams);
										fetchContract();
										refetchInvoices();
									}}
								/>
							</>
						)}
					</Grid>
				</Container>
			</Authenticated>
		</ContextInvoices.Provider>
	);
};
